import * as _ from 'lodash';
import { useCallback, useMemo, useState, type VoidFunctionComponent } from 'react';

import { FULL_SECURITY, REPORTS_V3, SBOM_SHARING } from '../../../../../shared/featureFlags/flagNames';
import { BinaryFetcherName } from '../../../../../shared/fetchers';
import Locator from '../../../../../shared/Locator';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { usePlanSelectorContext } from '../../../../contexts/PlanSelectorContext';
import { useParams } from '../../../../contexts/RouterContext';
import { useAddToast } from '../../../../contexts/ToastContext';
import { useIsFeatureEnabled, useIsSecurityEnabled } from '../../../../helpers/features';
import { useCurrentProject } from '../../../../hooks/api/projects/useCurrentProject';
import { useCurrentRevision } from '../../../../hooks/api/revisions/useCurrentRevision';
import { useRebuildUserRevision } from '../../../../hooks/api/revisions/useRebuildRevision';
import { useHasPermission } from '../../../../shared/permissions';
import { isAuthed, nuc, openInNewTab } from '../../../../utils';
import Button from '../../../generic/Button';
import PaidFeatureGate from '../../../generic/Gates/PaidFeatureGate';
import { Icon, icons } from '../../../generic/Icon';
import { Menu, MenuItem, SubMenu } from '../../../generic/Menu';
import { MenuItemLink } from '../../../generic/Menu/MenuItemLink';
import { apiSupportFeature, vulnManagementFeature } from '../../../settings/billing/features/consts';
import { useCanDisplaySecurityOptions } from '../../generic/hooks';
import { ReportCategories, ReportCategoryTitles } from '../../generic/ReportGenerationV2/utils';
import { ShareProjectModal } from '../../summary/SBOMSharing';
import type { RevisionRouteParams } from '../../projectDashboardTypes';

import styles from './ActionsMenu.module.less';

// The V3 reports flow uses camelCase slugs for some categories where the
// legacy ReportCategories use kebab-case. Mirrors the override map in
// ProjectSubnavItemReports so both surfaces resolve to the same URL.
const reportsV3SlugOverrides: Partial<Record<string, string>> = {
  [ReportCategories.RemediationGuidance]: 'remediationGuidance',
};

// Reports that are fully premium and may need a paid-feature gate when
// FULL_SECURITY is off (matches the subnav's premium set).
const premiumOnlyReports = new Set<string>([ReportCategories.RemediationGuidance]);

type ReportEntry = {
  category: string;
  title: string;
  slug: string;
  isPremiumOnly: boolean;
};

const buildEnabledReports = (
  isSecurityAndPDFReportsEnabled: boolean,
  isSecurityEnabled: boolean,
  isBinaryProject: boolean,
  isNewReportsFlowEnabled: boolean,
): ReportEntry[] => {
  const excludedReports = new Set<string>();
  if (!isSecurityAndPDFReportsEnabled) {
    excludedReports.add(ReportCategories.Vulnerability);
  }
  if (!isSecurityEnabled || isBinaryProject) {
    excludedReports.add(ReportCategories.RemediationGuidance);
  }

  return Object.entries(ReportCategoryTitles).reduce<ReportEntry[]>((acc, [category, title]) => {
    if (excludedReports.has(category)) {
      return acc;
    }
    const slug = isNewReportsFlowEnabled ? (reportsV3SlugOverrides[category] ?? category) : category;
    acc.push({
      category,
      title,
      slug,
      isPremiumOnly: premiumOnlyReports.has(category),
    });
    return acc;
  }, []);
};

type ReportMenuEntryProps = {
  entry: ReportEntry;
  baseRevisionURL: string;
  isFullSecurityEnabled: boolean;
};

const ReportMenuEntry: VoidFunctionComponent<ReportMenuEntryProps> = ({
  entry,
  baseRevisionURL,
  isFullSecurityEnabled,
}) => {
  const to = `${baseRevisionURL}/reports/${entry.slug}`;
  if (entry.isPremiumOnly) {
    return (
      <PaidFeatureGate
        isRestricted={!isFullSecurityEnabled}
        modalFocusFeature={vulnManagementFeature}
        hidePill
      >
        <MenuItemLink to={to}>{entry.title}</MenuItemLink>
      </PaidFeatureGate>
    );
  }
  return <MenuItemLink to={to}>{entry.title}</MenuItemLink>;
};

type Props = {
  baseRevisionURL: string;
};

export const ActionsMenu: VoidFunctionComponent<Props> = ({ baseRevisionURL }) => {
  const params = useParams<RevisionRouteParams>();
  const { data: project } = useCurrentProject();
  const { data: revision } = useCurrentRevision();
  const { showPlanSelectorModal } = usePlanSelectorContext();
  const hasPermission = useHasPermission();
  const addToast = useAddToast();
  const { mutateAsync: rebuild, isPending: isRebuilding } = useRebuildUserRevision();

  const isSecurityAndPDFReportsEnabled = useCanDisplaySecurityOptions();
  const isSecurityEnabled = useIsSecurityEnabled();
  const isFullSecurityEnabled = useIsFeatureEnabled(FULL_SECURITY);
  const isSBOMSharingEnabled = useIsFeatureEnabled(SBOM_SHARING);
  const isNewReportsFlowEnabled = useIsFeatureEnabled(REPORTS_V3);

  const projectId = params.project_id;
  const { fetcher } = useMemo(() => Locator.parse(projectId), [projectId]);
  const isBinaryProject = fetcher === BinaryFetcherName;

  const canCreateProjectReports =
    hasPermission({ resourceType: ResourceType.ProjectReportAny, action: Action.Create }) ||
    hasPermission({ resourceType: ResourceType.ProjectReport, action: Action.Create, projectId });

  const canEditProject =
    hasPermission({ resourceType: ResourceType.ProjectAny, action: Action.Edit }) ||
    hasPermission({ resourceType: ResourceType.Project, action: Action.Edit, projectId });

  const isMediationRequired = !!project?.require_mediated_dependencies;
  const projectLocator = project?.locator;
  const revisionLocator = revision?.locator;

  const enabledReports = useMemo(
    () => buildEnabledReports(
      isSecurityAndPDFReportsEnabled,
      isSecurityEnabled,
      isBinaryProject,
      isNewReportsFlowEnabled,
    ),
    [isSecurityAndPDFReportsEnabled, isSecurityEnabled, isBinaryProject, isNewReportsFlowEnabled],
  );

  const [isShareModalVisible, setIsShareModalVisible] = useState(false);

  const shouldShowShareProject =
    canEditProject && isSBOMSharingEnabled && !!projectLocator && !!revisionLocator;
  const shouldShowRebuild =
    canEditProject && !isBinaryProject && !isMediationRequired && !!revisionLocator;

  const handleVisitApiEndpoint = useCallback(() => {
    if (!revisionLocator) {
      return;
    }
    if (isAuthed()) {
      openInNewTab(`/api/revisions/${nuc(revisionLocator)}`);
      return;
    }
    showPlanSelectorModal({ focusFeature: apiSupportFeature });
  }, [revisionLocator, showPlanSelectorModal]);

  const handleRebuild = useCallback(async () => {
    if (!revisionLocator) {
      return;
    }
    try {
      await rebuild({ revisionLocator });
    } catch {
      addToast({ type: 'error', content: 'Failed to start rebuild.' });
    }
  }, [revisionLocator, rebuild, addToast]);

  return (
    <>
      <Menu
        menuButton={
          <Button type="secondary" size="medium" onClick={_.noop}>
            <span className={styles.actionsLabel}>
              Actions
              <Icon icon={icons.chevronDown} />
            </span>
          </Button>
        }
        direction="bottom"
        align="end"
        gap={4}
      >
        {canCreateProjectReports && enabledReports.length > 0 && (
          <SubMenu
            direction="right"
            position="initial"
            label={
              <span className={styles.itemLabel}>
                <Icon icon={icons.filePlusO} />
                Generate Reports
              </span>
            }
          >
            {enabledReports.map((entry) => (
              <ReportMenuEntry
                key={entry.category}
                entry={entry}
                baseRevisionURL={baseRevisionURL}
                isFullSecurityEnabled={isFullSecurityEnabled}
              />
            ))}
          </SubMenu>
        )}
        <MenuItem onClick={handleVisitApiEndpoint}>
          <span className={styles.itemLabel}>
            <Icon icon={icons.api} />
            Visit API Endpoint
          </span>
        </MenuItem>
        {shouldShowShareProject && (
          <MenuItem onClick={() => setIsShareModalVisible(true)}>
            <span className={styles.itemLabel}>
              <Icon icon={icons.unfilledShare} />
              Share Project
            </span>
          </MenuItem>
        )}
        {shouldShowRebuild && (
          <MenuItem onClick={handleRebuild} disabled={isRebuilding}>
            <span className={styles.itemLabel}>
              <Icon icon={icons.refresh} />
              Rebuild & Mediate Deps
            </span>
          </MenuItem>
        )}
      </Menu>
      {shouldShowShareProject && (
        <ShareProjectModal
          isVisible={isShareModalVisible}
          onHide={() => setIsShareModalVisible(false)}
          projectLocator={projectLocator}
          revisionLocator={revisionLocator}
        />
      )}
    </>
  );
};
